import {
	type AbstractExperienceConfig,
	type EndStateConfig,
	perfNowOrTimestamp,
} from './abstract-experience';
import { UFOExperience } from './experience';

interface StreamingChunkTiming {
	duration: number;
	startTime: number;
}
interface WSConnectionTiming extends StreamingChunkTiming {
	key: string;
}

export class StreamingUFOExperience extends UFOExperience {
	private chunksResponseTime = new Map<number, StreamingChunkTiming>();
	private webSocketConnectionTiming: WSConnectionTiming | null = null;
	private ttfbTiming: StreamingChunkTiming | null = null;

	constructor(id: string, config: AbstractExperienceConfig, instanceId: null | string = null) {
		super(id, config, instanceId);
	}

	public startConnectionAttempt(): void {
		this.webSocketConnectionTiming = {
			key: 'ws_connection',
			startTime: perfNowOrTimestamp(),
			duration: 0,
		};
	}

	public success(config?: EndStateConfig): Promise<boolean | null> {
		this.addAverageChunkDuration();
		return super.success(config);
	}

	public failure(config?: EndStateConfig): Promise<boolean | null> {
		this.addAverageChunkDuration();
		return super.failure(config);
	}

	public abort(config?: EndStateConfig): Promise<boolean | null> {
		this.addAverageChunkDuration();
		return super.abort(config);
	}

	public connectionEstablished(): void {
		if (this.webSocketConnectionTiming) {
			this.webSocketConnectionTiming.duration =
				perfNowOrTimestamp() - this.webSocketConnectionTiming.startTime;
			this.addMetadata({
				ws_connection: { ...this.webSocketConnectionTiming },
			});
		}
		this.ttfbTiming = {
			startTime: perfNowOrTimestamp(),
			duration: 0,
		};
	}

	public reportTTFB(): void {
		if (this.ttfbTiming) {
			this.ttfbTiming.duration = perfNowOrTimestamp() - this.ttfbTiming.startTime;
			this.addMetadata({
				ttfb: { ...this.ttfbTiming },
			});
			this.updateChunkTiming(this.ttfbTiming, 1);
		}
	}

	public addExperienceTotalDuration(): void {
		this.addMetadata({
			total_duration: (this.metrics.endTime || 0) - (this.metrics.startTime || 0),
		});
	}

	private addAverageChunkDuration() {
		const durations = Array.from(this.chunksResponseTime.values())
			.map((chunk) => chunk.duration)
			.filter((duration) => duration > 0);
		const averageDurationInMilliseconds =
			durations.reduce((sum, milliseconds) => sum + milliseconds, 0) / durations.length;

		if (this.metrics.startTime) {
			this.markInlineResponse(this.metrics.startTime + averageDurationInMilliseconds);
		}
	}

	private updateChunkTiming(
		currentChunkTiming: StreamingChunkTiming,
		createdItemChunkIndex: number,
	) {
		this.chunksResponseTime.set(createdItemChunkIndex, {
			duration: perfNowOrTimestamp() - currentChunkTiming.startTime,
			startTime: currentChunkTiming.startTime,
		});
		this.chunksResponseTime.set(createdItemChunkIndex + 1, {
			duration: 0,
			startTime: perfNowOrTimestamp(),
		});
	}

	public reportRTT(createdItemChunkIndex: number): void {
		const currentChunkTiming = this.chunksResponseTime.get(createdItemChunkIndex);
		if (currentChunkTiming) {
			this.updateChunkTiming(currentChunkTiming, createdItemChunkIndex);
			this.addMetadata({
				chunks_rtt: Object.fromEntries(
					Array.from(this.chunksResponseTime.entries())
						.filter(([_, value]) => value.duration > 0)
						.map(([key, value]) => [`chunk_rtt_${key}`, value.duration]),
				),
			});
		}
	}
}
